import { useStore } from '@/lib/store';
import { intervalsAuth } from '@/lib/auth/IntervalsAuth';
import { supabase } from '@/lib/supabase';
import type { IntervalsActivity } from '@/types'; // Import from unified types
import { createLogger } from '@/lib/logger';
import { ExternalServiceError, ValidationError } from '@/lib/errors';
import { invariant } from '@/lib/assertions';

const log = createLogger('intervals-client');

export interface IntervalsEvent {
    id?: string;
    start_date_local: string;
    category: 'WORKOUT' | 'TARGET' | 'NOTE' | 'RACE_A' | 'RACE_B' | 'RACE_C';
    name: string;
    description?: string;
    type?: string;
    indoor?: boolean;
    color?: string;
    moving_time?: number;
    distance?: number;
    workout_doc?: WorkoutDoc;
    load_target?: number;
    time_target?: number;
    distance_target?: number;
    external_id?: string;
    sport_settings_id?: number;
}

export interface WorkoutDoc {
    steps: WorkoutStep[];
}

export interface WorkoutStep {
    type: 'warmup' | 'cooldown' | 'interval' | 'rest' | 'ramp';
    duration?: { value: number; unit: 'seconds' | 'minutes' | 'km' };
    target?: { type: 'pace' | 'power' | 'hr'; min?: number; max?: number };
    reps?: number;
    onDuration?: { value: number; unit: string };
    offDuration?: { value: number; unit: string };
}

export interface IntervalsAthlete {
    id: string;
    name: string;
    email?: string;
    profile_medium?: string;
    sport_settings?: SportSetting[];
    ftp?: number;
    icu_ftp?: number;
    lthr?: number;
    max_hr?: number;
    icu_max_hr?: number;
    icu_resting_hr?: number;
    weight?: number;
    height?: number;
    sex?: 'M' | 'F';
    icu_ctl?: number;
    icu_atl?: number;
    icu_tsb?: number;
    icu_lthr?: number;
    lthr_pace?: number;
    icu_lthr_pace?: number;
    icu_threshold_pace?: number;
    // Global settings fallbacks
    power_zones?: number[];
    hr_zones?: number[];
    pace_zones?: number[];
    power_zone_names?: string[];
    hr_zone_names?: string[];
    pace_zone_names?: string[];
    dob?: string;
    birthdate?: string;
    date_of_birth?: string;
}

export interface SportSetting {
    id: number;
    types: string[];
    default_indoor_type?: string;
    pace_units?: string;
    ftp?: number;
    lthr?: number;
    threshold?: number;
    threshold_pace?: number;
    max_hr?: number;
    zone_power?: {
        name: string;
        pct: number;
    }[];
    zone_hr?: {
        name: string;
        pct: number;
    }[];
    zone_pace?: {
        name: string;
        pct: number;
    }[];
    // Legacy/Flat array formats often used in API
    power_zones?: number[];
    hr_zones?: number[];
    pace_zones?: number[];
    power_zone_names?: string[];
    hr_zone_names?: string[];
    pace_zone_names?: string[];
}

export interface IntervalsWellness {
    date: string;
    // RHR
    rhr?: number;
    restingHR?: number;
    // HRV
    hrv?: number;
    hrvSDNN?: number;
    // Sleep
    sleep_quality?: number;
    sleepQuality?: number;
    sleep_secs?: number;
    sleepSecs?: number;
    // Readiness/Wellness
    fatigue?: number;
    soreness?: number;
    motivation?: number;
    readiness?: number;
    // Body
    weight?: number;
    // Fitness (derived)
    ctl?: number;
    atl?: number;
    tsb?: number;
    icu_ctl?: number;
    icu_atl?: number;
    icu_tsb?: number;
}

// ============================================================================
// API CLIENT CLASS
// ============================================================================

class IntervalsClient {
    private baseUrl = 'https://intervals.icu/api/v1';

    /**
     * Get auth header
     */
    private getAuthHeader(): string {
        const { accessToken } = useStore.getState().intervals;

        if (accessToken) {
            return `Bearer ${accessToken}`;
        }

        throw new ValidationError('Intervals.icu connection not found. Please connect via OAuth in Settings.');
    }

    /**
     * Get athlete ID
     */
    private getAthleteId(): string {
        const { athleteId } = useStore.getState().intervals;
        // If athleteId is missing or "pending", we use "0" as it refers to the 
        // authenticated user's own calendar in Intervals.icu.
        if (!athleteId || athleteId === 'pending') {
            log.info('getAthleteId', 'No specific athleteId found, using "0" (self)');
            return '0';
        }
        return athleteId;
    }

    /**
     * Delay helper
     */
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Make authenticated request with retry logic for 429
     */
    private async request<T>(
        endpoint: string,
        options: RequestInit = {},
        retryCount = 0
    ): Promise<T> {
        // We only trace the top-level request, not recursive retries if possible, 
        // but simple tracing here is fine.
        return log.trace('request', async () => {
            const url = `${this.baseUrl}${endpoint}`;

            try {
                const response = await fetch(url, {
                    ...options,
                    headers: {
                        'Authorization': this.getAuthHeader(),
                        'Content-Type': 'application/json',
                        ...options.headers,
                    },
                });

                // Handle Rate Limiting (429)
                if (response.status === 429 && retryCount < 3) {
                    const waitTime = Math.pow(2, retryCount) * 1000;
                    log.warn('request', `429 Rate Limited. Retrying in ${waitTime}ms...`, { waitTime, retryCount });
                    await this.delay(waitTime);
                    return this.request(endpoint, options, retryCount + 1);
                }

                // Handle Token Expiry (401)
                // Only try refresh if we have a refresh token and haven't already retried for this reason
                let isRetry = false;
                if (options.headers) {
                    // Safe check for Headers object method
                    const headers = options.headers as unknown;
                    if (
                        typeof headers === 'object' &&
                        headers !== null &&
                        'get' in headers &&
                        typeof (headers as { get: unknown }).get === 'function'
                    ) {
                        isRetry = !!(headers as Headers).get('X-Retry');
                    } else if (Array.isArray(options.headers)) {
                        isRetry = (options.headers as string[][]).some(([key]) => key === 'X-Retry');
                    } else {
                        isRetry = !!(options.headers as Record<string, string>)['X-Retry'];
                    }
                }

                if (response.status === 401 && !isRetry) {
                    const { refreshToken } = useStore.getState().intervals;
                    if (refreshToken) {
                        try {
                            log.info('request', '401 Unauthorized. Attempting token refresh...');
                            const newTokens = await intervalsAuth.refreshToken(refreshToken);
                            const previous = useStore.getState().intervals;
                            const nextRefreshToken = newTokens.refresh_token || refreshToken;
                            const nextScope = newTokens.scope || previous.scope || '';

                            // Update store with new tokens
                            useStore.getState().setIntervalsCredentials({
                                accessToken: newTokens.access_token,
                                refreshToken: nextRefreshToken,
                                expiresAt: Math.floor(Date.now() / 1000) + newTokens.expires_in,
                                scope: nextScope,
                            });

                            // Persist refreshed OAuth tokens immediately
                            const { error: oauthRpcError } = await supabase.rpc('set_user_intervals_oauth_tokens', {
                                p_access_token: newTokens.access_token,
                                p_refresh_token: nextRefreshToken,
                                p_expires_at: Math.floor(Date.now() / 1000) + newTokens.expires_in,
                                p_scope: nextScope,
                            });
                            if (oauthRpcError) {
                                log.warn('request', 'Failed to persist refreshed OAuth tokens', { error: oauthRpcError });
                            }

                            // Retry original request with new token
                            const newHeaders = new Headers(options.headers);
                            newHeaders.set('X-Retry', 'true');

                            return this.request(endpoint, {
                                ...options,
                                headers: newHeaders
                            }, retryCount);

                        } catch (refreshErr) {
                            log.error('request', 'Token refresh failed', { error: refreshErr instanceof Error ? refreshErr.message : String(refreshErr) });
                        }
                    }
                }

                if (!response.ok) {
                    const errorText = await response.text();
                    throw new ExternalServiceError('intervals.icu', `API error: ${response.status} - ${errorText}`);
                }

                const text = await response.text();
                if (!text || text.trim().length === 0) {
                    return {} as T;
                }

                return JSON.parse(text);
            } catch (error) {
                if (error instanceof Error && error.message.includes('429') && retryCount < 3) {
                    const waitTime = Math.pow(2, retryCount) * 1000;
                    await this.delay(waitTime);
                    return this.request(endpoint, options, retryCount + 1);
                }
                throw error instanceof ExternalServiceError ? error : new ExternalServiceError('intervals.icu', error instanceof Error ? error.message : String(error), error instanceof Error ? error : undefined);
            }
        });
    }

    // =========================================================================
    // ATHLETE
    // =========================================================================

    /**
     * Fetch sport settings for a specific sport type
     */
    async getSportSettings(sportType: string): Promise<SportSetting | null> {
        const athleteId = this.getAthleteId();
        try {
            return await this.request<SportSetting>(`/athlete/${athleteId}/sport-settings/${sportType}`);
        } catch (error) {
            log.warn('getSportSettings', `Could not fetch sport settings for ${sportType}`, { sportType, error: error instanceof Error ? error.message : String(error) });
            return null;
        }
    }

    /**
     * Fetch all sport settings for the athlete
     */
    async getAllSportSettings(): Promise<SportSetting[]> {
        const athleteId = this.getAthleteId();
        try {
            return await this.request<SportSetting[]>(`/athlete/${athleteId}/sport-settings`);
        } catch (error) {
            log.warn('getAllSportSettings', 'Could not fetch all sport settings', { error: error instanceof Error ? error.message : String(error) });
            return [];
        }
    }

    /**
     * Update sport settings for a specific sport type
     * @param sportType e.g. "Run", "Ride"
     * @param settings The settings payload (partial)
     */
    async updateSportSettings(sportType: string, settings: Partial<SportSetting>): Promise<SportSetting> {
        const athleteId = this.getAthleteId();
        // PUT /athlete/{id}/sport-settings/{type}
        return await this.request<SportSetting>(`/athlete/${athleteId}/sport-settings/${sportType}`, {
            method: 'PUT',
            body: JSON.stringify(settings),
        });
    }

    /**
     * Fetch athlete profile and settings
     * Combines the basic athlete endpoint with sport settings
     */
    async getAthlete(): Promise<IntervalsAthlete> {
        const athleteId = this.getAthleteId();

        // Fetch basic athlete data
        const athlete = await this.request<IntervalsAthlete>(`/athlete/${athleteId}`);

        // Log raw response for debugging
        log.debug('getAthlete', 'Raw athlete response', { athlete });

        invariant(athlete && typeof athlete.id === 'string', 'Invalid athlete response: missing ID');

        // If sport_settings is already populated, return as-is
        if (athlete.sport_settings && athlete.sport_settings.length > 0) {
            return athlete;
        }

        // Otherwise, try to fetch sport settings separately
        log.info('getAthlete', 'sport_settings empty, fetching separately...');
        const sportSettings = await this.getAllSportSettings();

        if (sportSettings.length > 0) {
            athlete.sport_settings = sportSettings;
        }

        return athlete;
    }

    /**
     * Test connection with current credentials
     */
    async testConnection(): Promise<{ success: boolean; athlete?: IntervalsAthlete; error?: string }> {
        try {
            const athlete = await this.getAthlete();
            return { success: true, athlete };
        } catch (error) {
            // SILENT: connection test — failure is an expected result, but we clarify it in logs
            log.warn('testConnection', 'Connection test failed', { error: error instanceof Error ? error.message : String(error) });
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error'
            };
        }
    }

    // =========================================================================
    // EVENTS (WORKOUTS)
    // =========================================================================

    /**
     * Fetch events for a date range
     */
    async getEvents(
        oldest: string,
        newest: string,
        category?: 'WORKOUT' | 'TARGET' | 'NOTE'
    ): Promise<IntervalsEvent[]> {
        const athleteId = this.getAthleteId();
        let url = `/athlete/${athleteId}/events?oldest=${oldest}&newest=${newest}`;
        if (category) url += `&category=${category}`;
        const response = await this.request<IntervalsEvent[]>(url);
        invariant(Array.isArray(response), 'Invalid events response: expected array');
        return response;
    }

    /**
     * Push events in bulk (upsert)
     * Alias for uploadEvents to match UI usage
     */
    async pushEvents(events: IntervalsEvent[]): Promise<{ success: boolean; count: number }> {
        return this.uploadEvents(events);
    }

    /**
     * Upload events in bulk (upsert)
     */
    async uploadEvents(events: IntervalsEvent[]): Promise<{ success: boolean; count: number }> {
        if (!events.length) return { success: true, count: 0 };

        const athleteId = this.getAthleteId();
        await this.request(`/athlete/${athleteId}/events/bulk?upsert=true`, {
            method: 'POST',
            body: JSON.stringify(events),
        });

        return { success: true, count: events.length };
    }

    /**
     * Delete events in bulk
     */
    async deleteEvents(events: IntervalsEvent[]): Promise<{ success: boolean; deleted: number }> {
        if (!events.length) return { success: true, deleted: 0 };

        const athleteId = this.getAthleteId();
        const doomedEvents = events
            .filter(e => e.id)
            .map(e => ({ id: e.id }));

        if (!doomedEvents.length) return { success: true, deleted: 0 };

        try {
            await this.request(`/athlete/${athleteId}/events/bulk-delete`, {
                method: 'PUT',
                body: JSON.stringify(doomedEvents),
            });
            return { success: true, deleted: doomedEvents.length };
        } catch (e) {
            log.error('deleteEvents', 'Bulk delete failed', { error: e instanceof Error ? e.message : String(e) });
            // Fallback to sequential delete if bulk fails (though it shouldn't)
            let deleted = 0;
            for (const event of events) {
                if (!event.id) continue;
                try {
                    if (deleted > 0) await this.delay(200);
                    await this.request(`/athlete/${athleteId}/events/${event.id}`, {
                        method: 'DELETE',
                    });
                    deleted++;
                } catch (err) {
                    log.warn('deleteEvents', `Failed to delete event ${event.id}`, { eventId: event.id, error: err instanceof Error ? err.message : String(err) });
                }
            }
            return { success: true, deleted };
        }
    }

    /**
     * Delete events in a specific date range
     * This is useful for clearing a week before re-uploading
     */
    async deleteEventsInRange(start: string, end: string): Promise<{ success: boolean }> {
        this.getAthleteId(); // Validates connection
        // 1. Get events in range
        const events = await this.getEvents(start, end, 'WORKOUT');

        // 2. Delete them
        await this.deleteEvents(events);

        return { success: true };
    }

    // =========================================================================
    // ACTIVITIES
    // =========================================================================

    /**
     * Fetch activities for a date range
     */
    async getActivities(oldest: string, newest: string): Promise<IntervalsActivity[]> {
        const athleteId = this.getAthleteId();
        return this.request<IntervalsActivity[]>(
            `/athlete/${athleteId}/activities?oldest=${oldest}&newest=${newest}`
        );
    }

    /**
     * Fetch best efforts for the athlete
     */
    async getBestEfforts(): Promise<unknown[]> {
        const athleteId = this.getAthleteId();
        try {
            return await this.request<unknown[]>(`/athlete/${athleteId}/best-efforts`);
        } catch (e) {
            // 404 is common if the athlete has no best efforts recorded or is new
            const is404 = e instanceof Error && e.message.includes('404');
            if (is404) {
                log.info('getBestEfforts', 'Best efforts not found for athlete (expected for some profiles)');
            } else {
                log.warn('getBestEfforts', 'Error fetching best efforts', { error: e instanceof Error ? e.message : String(e) });
            }
            return [];
        }
    }

    // =========================================================================
    // WELLNESS
    // =========================================================================

    /**
     * Fetch wellness data for a date range
     */
    async getWellness(oldest: string, newest: string): Promise<IntervalsWellness[]> {
        const athleteId = this.getAthleteId();
        return this.request<IntervalsWellness[]>(
            `/athlete/${athleteId}/wellness?oldest=${oldest}&newest=${newest}`
        );
    }

    // =========================================================================
    // UTILITY
    // =========================================================================

    /**
     * Fetch the user's current CTL from the latest wellness data
     */
    async fetchCurrentCTL(): Promise<number | null> {
        try {
            const today = new Date().toISOString().split('T')[0];
            const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

            // Fetch wellness for last 30 days to ensure we get a recent value
            const wellness = await this.getWellness(thirtyDaysAgo, today);

            if (!wellness || wellness.length === 0) return null;

            // Sort by date descending
            const sorted = wellness.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

            // Find first record with ctl (using 'ctl' or 'icu_ctl' if available, though wellness usually has 'ctl')
            // Note: The interface definition might need 'ctl' added if not present.
            // Let's check the interface above.
            const latest = sorted.find(w => w.ctl !== undefined || w.icu_ctl !== undefined);
            if (latest) {
                return latest.ctl ?? latest.icu_ctl ?? null;
            }

            return null;
        } catch (e) {
            log.error('fetchCurrentCTL', 'Failed to fetch CTL', { error: e instanceof Error ? e.message : String(e) });
            return null;
        }
    }

    /**
     * Find the WeightTraining sport settings ID
     */
    async getStrengthSportId(): Promise<number | null> {
        try {
            const athlete = await this.getAthlete();
            const setting = athlete.sport_settings?.find(s =>
                s.types?.includes('WeightTraining')
            );
            return setting?.id ?? null;
        } catch {
            // SILENT: best-effort lookup, null is a valid fallback
            return null;
        }
    }
}

// Export singleton instance
export const intervalsClient = new IntervalsClient();

// Export class for testing
export { IntervalsClient };
